// Data Pipeline (flow), copilot and the Meta delivery contract.
//
// The flow reads are split across namespaces by what they actually are: the
// live snapshot and tap feed are realtime polls; history/recent tickets are
// on-demand projections, and lineage polls only while an item is in flight.

import { api } from '@/api/client';
import { CADENCE, FRESHNESS, defineQuery } from '@/api/resource';
import { buildQs } from '@/api/query-string';
import { traceState } from '@/lib/flow-lineage';
import type {
  TicketFlowPage,
  MetaDeliveryContract,
} from '@/api/types.gen';
import type {
  CopilotStatus,
  PipelineLineageResponse,
  ConversionsHistoryResponse,
  FlowLivePage,
  PipelineFlowSnapshot,
  TicketsHistoryResponse,
  WebEventsHistoryResponse,
} from '@/api/types';

export interface FlowHistoryQuery {
  from: string;
  to: string;
  granularity: string;
  productionId?: string | null;
  pixelId?: string | null;
}

/** The snapshot's poll interval; the page footer quotes this constant, never a literal. */
export const FLOW_SNAPSHOT_POLL_MS = CADENCE.snapshot;
/** The live feed's poll interval; the page footer quotes this constant, never a literal. */
export const FLOW_FEED_POLL_MS = CADENCE.fast;
/** A trace waiting for its enqueue (the ticket lane runs every 6 h) polls once a minute. */
export const LINEAGE_AWAITING_ENQUEUE_POLL_MS = 60_000;
/** A settled trace still reads its "Ticket now" once a minute while the page is visible,
 *  so a later UID2 or line change appears without a reload. */
export const LINEAGE_SETTLED_POLL_MS = 60_000;

export const flowSnapshot = defineQuery({
  namespace: 'realtime',
  name: 'flow.snapshot',
  volatile: true,
  keyOf: (productionId: string | null) => [productionId],
  cadence: FLOW_SNAPSHOT_POLL_MS,
  request: (productionId: string | null, signal) =>
    api.get<PipelineFlowSnapshot>(
      `/flow/snapshot${buildQs({ production_id: productionId ?? undefined })}`,
      { signal },
    ),
});

/**
 * The live tap feed. Full pages, never a cursor — a cursor wedges after a tap
 * restart resets the seq space, and never re-serves rows mutated server-side.
 *
 * Scoped to a production server-side when one is selected, so the server's
 * newest-200 window counts that production's events rather than everyone's
 * (a busy production used to crowd a quiet one out of the page entirely). The
 * production is part of the key: two scopes never share a slot.
 *
 * `paused` is expressed as a call-site cadence override rather than a field on
 * the resource: pausing is a property of one screen's UI state, not of the
 * read. `useFlowFeed` passes `{ cadence: paused ? false : undefined }`.
 */
export const flowFeed = defineQuery({
  namespace: 'realtime',
  name: 'flow.feed',
  volatile: true,
  keyOf: (productionId: string | null) => [productionId],
  cadence: FLOW_FEED_POLL_MS,
  request: (productionId: string | null, signal) =>
    api.get<FlowLivePage>(
      `/flow/live${buildQs({ production_id: productionId ?? undefined })}`,
      { signal },
    ),
});

/**
 * End-to-end per-stage lineage for one correlation_id.
 *
 * The cadence is a function of the data: look every few seconds while the item
 * is still in flight, and once a minute after `traceState` says every
 * destination the queue lists is terminal (or the trace is historical). The diagram overlay and the
 * panel's "Still in flight" line read the same predicate. This is the one
 * resource whose poll answers a question about its own payload, which is why
 * `Cadence` accepts a function at all.
 */
export const flowLineage = defineQuery({
  // The taxonomy guard caught this as a persisted poll in the retired cache.
  // A trace snapshot has a ten-second useful life, so it is explicitly volatile.
  namespace: 'realtime',
  name: 'flow.lineage',
  volatile: true,
  keyOf: (correlationId: string | null) => [correlationId],
  cadence: (data: PipelineLineageResponse | undefined) => {
    const state = traceState(data);
    // Settled: the journey is over, but the ticket as SAM holds it can still move
    // (a UID2 mapping, a refund line). The poll pauses with the tab, as every
    // realtime read does.
    if (state === 'settled') return LINEAGE_SETTLED_POLL_MS;
    // Waiting for the ticket lane's 6-hourly enqueue: look once a minute.
    return state === 'awaiting_enqueue' ? LINEAGE_AWAITING_ENQUEUE_POLL_MS : CADENCE.snapshot;
  },
  request: (correlationId: string | null, signal) =>
    api.get<PipelineLineageResponse>(
      `/flow/lineage${buildQs({ correlation_id: correlationId ?? undefined })}`,
      { signal },
    ),
});

/** Redacted recent tickets for the encore context panel — fetched on select. */
export const flowTicketsRecent = defineQuery({
  namespace: 'authoritative',
  name: 'flow.ticketsRecent',
  volatile: true,
  keyOf: (productionId: string | null) => [productionId],
  request: (productionId: string | null, signal) =>
    api.get<TicketFlowPage>(
      `/flow/tickets-recent${buildQs({ production_id: productionId ?? undefined })}`,
      { signal },
    ),
});

function historyQs(q: FlowHistoryQuery): string {
  return buildQs({
    from: q.from,
    to: q.to,
    granularity: q.granularity,
    production_id: q.productionId ?? undefined,
    pixel_id: q.pixelId ?? undefined,
  });
}

/**
 * The key deliberately normalises `undefined` to `null` so a query built with
 * an absent `productionId` and one built with an explicit `null` land in the
 * same slot. The hook did this by hand into a separate `keyQ` object, which
 * meant the key and the querystring were built from two different expressions.
 */
const historyKey = (q: FlowHistoryQuery) => [{
  from: q.from,
  to: q.to,
  granularity: q.granularity,
  productionId: q.productionId ?? null,
  pixelId: q.pixelId ?? null,
}];

export const flowHistoryWebEvents = defineQuery({
  namespace: 'derived',
  name: 'flow.historyWebEvents',
  volatile: true,
  keyOf: historyKey,
  request: (q: FlowHistoryQuery, signal) =>
    api.get<WebEventsHistoryResponse>(`/flow/history/web-events${historyQs(q)}`, { signal }),
});

export const flowHistoryTickets = defineQuery({
  namespace: 'derived',
  name: 'flow.historyTickets',
  volatile: true,
  keyOf: historyKey,
  request: (q: FlowHistoryQuery, signal) =>
    api.get<TicketsHistoryResponse>(`/flow/history/tickets${historyQs(q)}`, { signal }),
});

export const flowHistoryConversions = defineQuery({
  namespace: 'derived',
  name: 'flow.historyConversions',
  volatile: true,
  keyOf: historyKey,
  request: (q: FlowHistoryQuery, signal) =>
    api.get<ConversionsHistoryResponse>(`/flow/history/conversions${historyQs(q)}`, { signal }),
});

/**
 * The single copilot feature gate and Daily Briefing source.
 *
 * Volatile, not configuration-backed: no data revision ever moves for it, so a
 * domain here would hold a flag flip or a fresh briefing until an unrelated
 * configuration write. The server reads it live on every call (nothing caches
 * it since the briefing moved to a background run, 2026-11-01); five minutes
 * of freshness only spares the many screens that read the gate a request per
 * mount, and focus refetch surfaces a server-side flip on the next tab focus.
 *
 * The cadence is a function of the payload: while a briefing is being written
 * the status is watched closely, and it stops on the server's own
 * `generating: false` (an optimistic `true` from `useRegenerateBriefing` keeps
 * it polling until then). Nothing polls otherwise.
 */
export const copilotStatus = defineQuery({
  namespace: 'authoritative',
  name: 'copilot.status',
  volatile: true,
  keyOf: () => [],
  freshness: FRESHNESS.fiveMinutes,
  refetchOnWindowFocus: true,
  cadence: (data: CopilotStatus | undefined) => (data?.generating === true ? CADENCE.urgent : false),
  request: (_: void, signal) => api.get<CopilotStatus>('/copilot/status', { signal }),
});

export const metaDeliveryContract = defineQuery({
  namespace: 'authoritative',
  name: 'meta.deliveryContract',
  domains: ['configuration'],
  keyOf: () => [],
  request: (_: void, signal) =>
    api.get<MetaDeliveryContract>('/meta/delivery-contract', { signal }),
});
